import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import './styles/global.css';
import './styles/layout.css';
import './styles/common.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { loadAndApplySiteInfo } from './utils/siteInfo';
import { installGlobalTooltip } from './utils/tooltip';
import { PermissionProvider, usePermission } from './context/PermissionContext';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import EstimateListPage from './pages/EstimateListPage';
import EstimateNewPage from './pages/EstimateNewPage';
import EstimateManpowerNewPage from './pages/EstimateManpowerNewPage';
import EstimatePrintPage from './pages/EstimatePrintPage';
import EstimateSideBySidePage from './pages/EstimateSideBySidePage';
import SurveyJobsPage from './pages/SurveyJobsPage';
import ServicePackagesPage from './pages/ServicePackagesPage';
import ServicePackageNewPage from './pages/ServicePackageNewPage';
import ApprovalsPage from './pages/ApprovalsPage';
import BundleListPage from './pages/BundleListPage';
import BundleDetailPage from './pages/BundleDetailPage';
import BundleNewPage from './pages/BundleNewPage';
import ProductListPage from './pages/ProductListPage';
import ProductNewPage from './pages/ProductNewPage';
import PartsPage from './pages/PartsPage';
import CustomersPage from './pages/CustomersPage';
import OrganizationPage from './pages/OrganizationPage';
import UsersPage from './pages/UsersPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SettingsPage from './pages/SettingsPage';

function ProtectedRoute({ children }) {
  const { user, loading, mustChangePassword, passwordChanged } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>로딩 중...</div>;
  if (!user) return <Navigate to="/login" />;
  if (mustChangePassword) {
    return <ChangePasswordPage isForced={true} onComplete={passwordChanged} />;
  }
  return children;
}

function MenuGuard({ menu, children }) {
  const { canAccessMenu } = usePermission();
  if (!canAccessMenu(menu)) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>접근 권한이 없습니다</div>
        <div className="text-muted">이 메뉴에 대한 접근 권한이 없습니다. 관리자에게 문의하세요.</div>
      </div>
    );
  }
  return children;
}

function RedirectServicePkgEdit() {
  const { id } = useParams();
  return <Navigate to={`/services/${id}/edit`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/estimates/print" element={<ProtectedRoute><PermissionProvider><EstimatePrintPage /></PermissionProvider></ProtectedRoute>} />
      <Route path="/estimates/side-by-side" element={<ProtectedRoute><PermissionProvider><EstimateSideBySidePage /></PermissionProvider></ProtectedRoute>} />
      <Route element={<ProtectedRoute><PermissionProvider><AppLayout /></PermissionProvider></ProtectedRoute>}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/product-estimate" element={<MenuGuard menu="제품 견적서"><EstimateListPage key="goods" businessType="goods" /></MenuGuard>} />
        <Route path="/service-estimate" element={<MenuGuard menu="서비스견적서"><EstimateListPage key="manpower" businessType="manpower" /></MenuGuard>} />
        <Route path="/product-estimate/new" element={<MenuGuard menu="제품 견적서"><EstimateNewPage /></MenuGuard>} />
        <Route path="/service-estimate/new" element={<MenuGuard menu="서비스견적서"><EstimateManpowerNewPage /></MenuGuard>} />
        <Route path="/services" element={<MenuGuard menu="서비스 관리"><ServicePackagesPage /></MenuGuard>} />
        <Route path="/services/new" element={<MenuGuard menu="서비스 관리"><ServicePackageNewPage /></MenuGuard>} />
        <Route path="/services/:id/edit" element={<MenuGuard menu="서비스 관리"><ServicePackageNewPage /></MenuGuard>} />
        {/* 구 경로 호환 — 기존 북마크/링크 자동 리디렉트 */}
        <Route path="/estimates" element={<Navigate to="/product-estimate" replace />} />
        <Route path="/service-estimates" element={<Navigate to="/service-estimate" replace />} />
        <Route path="/estimates/new" element={<Navigate to="/product-estimate/new" replace />} />
        <Route path="/estimates/manpower/new" element={<Navigate to="/service-estimate/new" replace />} />
        <Route path="/service-packages" element={<Navigate to="/services" replace />} />
        <Route path="/service-packages/new" element={<Navigate to="/services/new" replace />} />
        <Route path="/service-packages/:id/edit" element={<RedirectServicePkgEdit />} />
        <Route path="/survey-jobs" element={<MenuGuard menu="직무별 가격 관리"><SurveyJobsPage /></MenuGuard>} />
        <Route path="/approvals" element={<MenuGuard menu="승인상태"><ApprovalsPage /></MenuGuard>} />
        <Route path="/bundles" element={<MenuGuard menu="제품 묶음"><BundleListPage /></MenuGuard>} />
        <Route path="/bundles/new" element={<MenuGuard menu="제품 묶음"><BundleNewPage /></MenuGuard>} />
        <Route path="/bundles/:id" element={<MenuGuard menu="제품 묶음"><BundleDetailPage /></MenuGuard>} />
        <Route path="/bundles/:id/edit" element={<MenuGuard menu="제품 묶음"><BundleNewPage /></MenuGuard>} />
        <Route path="/products" element={<MenuGuard menu="상품 관리"><ProductListPage /></MenuGuard>} />
        <Route path="/products/new" element={<MenuGuard menu="상품 관리"><ProductNewPage /></MenuGuard>} />
        <Route path="/parts" element={<MenuGuard menu="자재 관리"><PartsPage /></MenuGuard>} />
        <Route path="/customers" element={<MenuGuard menu="고객 관리"><CustomersPage /></MenuGuard>} />
        <Route path="/organization" element={<MenuGuard menu="조직 관리"><OrganizationPage /></MenuGuard>} />
        <Route path="/users" element={<MenuGuard menu="사용자관리"><UsersPage /></MenuGuard>} />
        <Route path="/audit-logs" element={<MenuGuard menu="감사 로그"><AuditLogsPage /></MenuGuard>} />
        <Route path="/settings" element={<MenuGuard menu="설정"><SettingsPage /></MenuGuard>} />
      </Route>
    </Routes>
  );
}

function App() {
  useEffect(() => { loadAndApplySiteInfo(); }, []);
  useEffect(() => installGlobalTooltip(), []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
